<template>
  <div class="product-list">
    <div class="page-header">
      <div>
        <h2 class="page-title">商品列表</h2>
        <p class="page-desc">管理您的商品，支持上架/下架、编辑、快速复制</p>
      </div>
      <el-button type="primary" size="large" @click="$router.push('/product/add')">
        <el-icon>
          <Plus />
        </el-icon>
        新增商品
      </el-button>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <el-input v-model="searchKey" placeholder="搜索商品名称或编号" prefix-icon="Search" clearable style="width: 220px"
        @keyup.enter="handleSearch" />
      <el-select v-model="branchFilter" placeholder="请选择门店" clearable filterable style="width: 180px"
        @change="onBranchFilterChange">
        <el-option label="全部门店" :value="null" />
        <el-option v-for="store in storeOptions" :key="store.id" :label="store.store_name" :value="store.id" />
      </el-select>
      <el-select v-model="storeBranchFilter" placeholder="请选择分店" clearable filterable style="width: 180px"
        :disabled="!branchFilter" @change="onStoreBranchFilterChange">
        <el-option label="全部分店" :value="null" />
        <el-option v-for="branch in branchOptions" :key="branch.id" :label="branch.branch_name" :value="branch.id" />
      </el-select>
      <el-select v-model="categoryFilter" placeholder="全部分类" clearable filterable style="width: 150px">
        <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
      </el-select>
      <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px">
        <el-option label="上架中" :value="1" />
        <el-option label="已下架" :value="0" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-value">{{ totalCount }}</div>
        <div class="stat-label">总商品数</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ onSaleCount }}</div>
        <div class="stat-label">上架中</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ totalSoldCount }}</div>
        <div class="stat-label">总销量</div>
      </div>
    </div>

    <!-- 商品表格 -->
    <el-table :data="productList" border v-loading="loading" style="width: 100%">
      <el-table-column label="商品信息" min-width="320">
        <template #default="{ row }">
          <div class="product-info">
            <el-image :src="row.cover_image || defaultImage" fit="cover" class="product-img" />
            <div class="product-detail">
              <div class="product-name">{{ row.product_name }}</div>
              <div class="product-code">编号：{{ row.product_no }}</div>
              <div class="product-category">分类：{{ row.category_name || '未分类' }}</div>
              <!-- 显示适用范围标签 -->
              <div class="product-scope">
                <el-tag v-if="row.store_branch_id" type="warning" size="small" class="scope-tag">
                  门店: {{ getStoreNameByBranchId(row.store_branch_id) }} / 分店: {{ getBranchName(row.store_branch_id) }}
                </el-tag>
                <el-tag v-else-if="row.branch_id" type="primary" size="small" class="scope-tag">
                  门店: {{ getStoreName(row.branch_id) }}
                </el-tag>
                <el-tag v-else type="info" size="small" class="scope-tag">
                  全局商品
                </el-tag>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="价格" width="160" align="center">
        <template #default="{ row }">
          <div class="price-info">
            <div class="sale-price">¥{{ row.sale_price }}</div>
            <div class="original-price">¥{{ row.original_price }}</div>
            <div class="discount" v-if="row.discount">{{ row.discount }}折</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="销量/库存" width="140" align="center">
        <template #default="{ row }">
          <div>销量：{{ row.sold_count || 0 }}</div>
          <div>库存：{{ row.stock === -1 ? '不限' : row.stock }}</div>
        </template>
      </el-table-column>

      <el-table-column label="商品类型" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.product_type === 1 ? 'success' : 'primary'" size="small">
            {{ row.product_type === 1 ? '团购商品' : '预约商品' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="有效期" width="200" align="center">
        <template #default="{ row }">
          <span v-if="row.valid_start_date && row.valid_end_date">
            {{ row.valid_start_date }} ~ {{ row.valid_end_date }}
          </span>
          <span v-else class="text-muted">长期有效</span>
        </template>
      </el-table-column>

      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
            {{ row.status === 1 ? '上架中' : '已下架' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="排序" width="80" align="center">
        <template #default="{ row }">
          {{ row.sort_order }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="260" fixed="right" align="center">
        <template #default="{ row }">
          <el-button type="primary" link size="small" @click="handleEdit(row)">
            <el-icon>
              <Edit />
            </el-icon> 编辑
          </el-button>
          <el-button type="success" link size="small" @click="copyProduct(row)">
            <el-icon>
              <CopyDocument />
            </el-icon> 复制
          </el-button>
          <el-button :type="row.status === 1 ? 'warning' : 'success'" link size="small" @click="toggleStatus(row)">
            <el-icon>
              <Switch />
            </el-icon> {{ row.status === 1 ? '下架' : '上架' }}
          </el-button>
          <el-popconfirm title="确定删除该商品吗？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" link size="small">
                <el-icon>
                  <Delete />
                </el-icon> 删除
              </el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" />
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Plus, Edit, Delete, Switch, CopyDocument } from '@element-plus/icons-vue'

export default {
  name: 'ProductListPage',
  components: { Plus, Edit, Delete, Switch, CopyDocument },
  setup() {
    const router = useRouter()
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)

    const loading = ref(false)
    const searchKey = ref('')
    const categoryFilter = ref(null)
    const statusFilter = ref(null)
    const branchFilter = ref(null)
    const storeBranchFilter = ref(null)
    const productList = ref([])
    const categories = ref([])
    const storeOptions = ref([])
    const branchOptions = ref([])
    const allBranchOptions = ref([])
    const defaultImage = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

    const pagination = reactive({
      page: 1,
      limit: 10,
      total: 0
    })

    // 统计数据
    const totalCount = ref(0)
    const onSaleCount = ref(0)
    const totalSoldCount = ref(0)

    // 获取门店名称
    const getStoreName = (branchId) => {
      const store = storeOptions.value.find(s => s.id === branchId)
      return store ? store.store_name : '未知门店'
    }

    // 获取分店名称
    const getBranchName = (storeBranchId) => {
      const branch = allBranchOptions.value.find(b => b.id === storeBranchId)
      return branch ? branch.branch_name : '未知分店'
    }

    // 根据分店ID获取所属门店名称
    const getStoreNameByBranchId = (storeBranchId) => {
      const branch = allBranchOptions.value.find(b => b.id === storeBranchId)
      if (branch) {
        const store = storeOptions.value.find(s => s.id === branch.branch_id)
        return store ? store.store_name : '未知门店'
      }
      return '未知门店'
    }

    // 获取门店列表
    const fetchStores = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          storeOptions.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取门店失败:', error)
      }
    }

    // 获取全部分店列表
    const fetchAllBranches = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          allBranchOptions.value = response.data.data.list || []
        }
      } catch (error) {
        console.error('获取分店失败:', error)
      }
    }

    // 获取分店列表（根据门店筛选）
    const fetchBranches = async (branchId) => {
      if (!branchId) {
        branchOptions.value = []
        return
      }
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, branch_id: branchId, limit: 100 }
        })
        if (response.data.code === 200) {
          branchOptions.value = response.data.data.list || []
        } else {
          branchOptions.value = []
        }
      } catch (error) {
        console.error('获取分店失败:', error)
        branchOptions.value = []
      }
    }

    // 获取分类列表（根据门店/分店筛选）
    const fetchCategories = async () => {
      try {
        const params = {
          agent_id: agentId.value,
          store_id: storeId.value,
          type: 'product',
          action: 'for_product'
        }
        if (branchFilter.value) {
          params.branch_id = branchFilter.value
        }
        if (storeBranchFilter.value) {
          params.branch_id = branchFilter.value
          params.store_branch_id = storeBranchFilter.value
        }
        const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, { params })
        if (response.data.code === 200) {
          categories.value = response.data.data
        }
      } catch (error) {
        console.error('获取分类失败:', error)
      }
    }

    // 门店筛选变化
    const onBranchFilterChange = async () => {
      storeBranchFilter.value = null
      categoryFilter.value = null
      if (branchFilter.value) {
        await fetchBranches(branchFilter.value)
      } else {
        branchOptions.value = []
      }
      await fetchCategories()
      pagination.page = 1
      await fetchData()
    }

    // 分店筛选变化
    const onStoreBranchFilterChange = async () => {
      categoryFilter.value = null
      await fetchCategories()
      pagination.page = 1
      await fetchData()
    }

    // 获取商品列表
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          action: 'for_list',  // 新增：使用新接口
          agent_id: agentId.value,
          store_id: storeId.value,
          page: pagination.page,
          limit: pagination.limit,
          search: searchKey.value,
          category_id: categoryFilter.value,
          status: statusFilter.value
        }

        if (branchFilter.value) {
          params.branch_id = branchFilter.value
        }
        if (storeBranchFilter.value) {
          params.store_branch_id = storeBranchFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, { params })

        if (response.data.code === 200) {
          productList.value = response.data.data.list.map(item => ({
            ...item,
            images: item.images ? (typeof item.images === 'string' ? JSON.parse(item.images) : item.images) : [],
            service_features: item.service_features ? (typeof item.service_features === 'string' ? JSON.parse(item.service_features) : item.service_features) : [],
            use_rules: item.use_rules ? (typeof item.use_rules === 'string' ? JSON.parse(item.use_rules) : item.use_rules) : {}
          }))
          pagination.total = response.data.data.pagination.total

          totalCount.value = pagination.total
          onSaleCount.value = productList.value.filter(p => p.status === 1).length
          totalSoldCount.value = productList.value.reduce((sum, p) => sum + (p.sold_count || 0), 0)
        }
      } catch (error) {
        console.error('获取商品列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    // 搜索
    const handleSearch = () => {
      pagination.page = 1
      fetchData()
    }

    // 重置搜索
    const resetSearch = () => {
      searchKey.value = ''
      categoryFilter.value = null
      statusFilter.value = null
      branchFilter.value = null
      storeBranchFilter.value = null
      branchOptions.value = []
      pagination.page = 1
      fetchCategories()
      fetchData()
    }

    // 编辑商品
    const handleEdit = (row) => {
      router.push(`/product/edit?id=${row.id}`)
    }

    // 复制商品
    const copyProduct = async (row) => {
      const loadingInstance = ElLoading.service({ text: '复制中...' })
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, id: row.id }
        })
        if (response.data.code === 200) {
          const product = response.data.data
          const copyData = {
            agent_id: agentId.value,
            store_id: storeId.value,
            product_name: `${product.product_name}(副本)`,
            sub_title: product.sub_title,
            category_id: product.category_id,
            category_name: product.category_name,
            product_type: product.product_type,
            form_id: product.form_id,
            original_price: product.original_price,
            sale_price: product.sale_price,
            discount: product.discount,
            stock: product.stock,
            fictitious_sold: 0,
            cover_image: product.cover_image,
            images: product.images,
            service_features: product.service_features,
            use_rules: product.use_rules,
            description: product.description,
            goods_details: product.goods_details,
            valid_start_date: product.valid_start_date,
            valid_end_date: product.valid_end_date,
            status: 0,
            sort_order: product.sort_order + 1,
            branch_id: product.branch_id,
            store_branch_id: product.store_branch_id
          }
          const createResponse = await axios.post(`${config.BASE_URL}/shop/api/product_api.php`, copyData)
          if (createResponse.data.code === 200) {
            ElMessage.success('复制成功')
            fetchData()
          } else {
            ElMessage.error(createResponse.data.message || '复制失败')
          }
        }
      } catch (error) {
        console.error('复制失败:', error)
        ElMessage.error('复制失败')
      } finally {
        loadingInstance.close()
      }
    }

    // 切换状态
    const toggleStatus = async (row) => {
      const loadingInstance = ElLoading.service({ text: '处理中...' })
      try {
        const newStatus = row.status === 1 ? 0 : 1
        // 使用 PATCH 方法
        const response = await axios.patch(`${config.BASE_URL}/shop/api/product_api.php`, {
          id: row.id,
          store_id: storeId.value,
          status: newStatus
        })
        if (response.data.code === 200) {
          ElMessage.success(newStatus === 1 ? '已上架' : '已下架')
          fetchData()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('切换状态失败:', error)
        ElMessage.error('操作失败')
      } finally {
        loadingInstance.close()
      }
    }

    // 删除商品
    const handleDelete = async (id) => {
      const loadingInstance = ElLoading.service({ text: '删除中...' })
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/product_api.php`, {
          data: { id, store_id: storeId.value }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchData()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error('删除失败')
      } finally {
        loadingInstance.close()
      }
    }

    onMounted(() => {
      fetchStores()
      fetchAllBranches()
      fetchCategories()
      fetchData()
    })

    return {
      loading,
      searchKey,
      categoryFilter,
      statusFilter,
      branchFilter,
      storeBranchFilter,
      productList,
      categories,
      storeOptions,
      branchOptions,
      pagination,
      defaultImage,
      totalCount,
      onSaleCount,
      totalSoldCount,
      getStoreName,
      getBranchName,
      getStoreNameByBranchId,
      onBranchFilterChange,
      onStoreBranchFilterChange,
      handleSearch,
      resetSearch,
      handleEdit,
      copyProduct,
      toggleStatus,
      handleDelete,
      fetchData
    }
  }
}
</script>

<style scoped lang="scss">
.product-list {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 24px;
    padding: 16px;
    background: #f5f7fa;
    border-radius: 12px;
  }

  .stat-cards {
    display: flex;
    gap: 20px;
    margin-bottom: 24px;

    .stat-card {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 12px;
      padding: 20px 30px;
      text-align: center;
      min-width: 150px;

      &:nth-child(2) {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      }

      &:nth-child(3) {
        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        color: #fff;
      }

      .stat-label {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.9);
        margin-top: 8px;
      }
    }
  }

  .product-info {
    display: flex;
    gap: 12px;
    align-items: flex-start;

    .product-img {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .product-detail {
      flex: 1;

      .product-name {
        font-weight: 500;
        margin-bottom: 4px;
      }

      .product-code,
      .product-category {
        font-size: 12px;
        color: #8c8c8c;
      }

      .product-scope {
        margin-top: 6px;

        .scope-tag {
          font-size: 10px;
        }
      }
    }
  }

  .price-info {
    .sale-price {
      font-size: 16px;
      font-weight: 600;
      color: #f56c6c;
    }

    .original-price {
      font-size: 12px;
      color: #8c8c8c;
      text-decoration: line-through;
    }

    .discount {
      font-size: 12px;
      color: #e6a23c;
    }
  }

  .text-muted {
    color: #8c8c8c;
    font-size: 12px;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
}
</style>